import { useMutation } from "@tanstack/react-query";
import { Link, type LinkProps } from "@tanstack/react-router";
import { ApiError } from "@wilco/api";
import { Button, IconArrowLeft, Notice, Plate } from "@wilco/ui";
import { useState, type ReactNode, type SubmitEvent } from "react";
import { serverErrorText, t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";

/*
 * Общий каркас формы создания и правки документа (handoff 3 ноября):
 * поля, проверка в браузере, отказ сервера problem+json на своём поле,
 * Idempotency-Key, отмена. Модуль даёт значения, проверку и запрос, каркас
 * держит состояние, ключ и показ ошибок.
 */

export type FieldErrors<V> = Partial<Record<keyof V & string, string>>;

/** Заголовок Idempotency-Key: повтор того же запроса сервер не выполнит дважды. */
export const idempotency = (key: string): Record<string, string> => ({ "Idempotency-Key": key });

export interface DocumentFormOptions<V, R> {
  initial: V;
  /** Проверка в браузере: ключ поля и текст. Пусто значит можно отправлять. */
  validate?: (values: V) => FieldErrors<V>;
  /** Запрос с ключом идемпотентности, один на попытку сохранить эти значения. */
  submit: (values: V, idempotencyKey: string) => Promise<R>;
  onSuccess: (result: R) => void;
  /**
   * Поле, к которому относится отказ сервера. По умолчанию args.field
   * ответа; без него отказ показывается над формой.
   */
  fieldOf?: (code: string, args: Record<string, unknown>) => (keyof V & string) | undefined;
}

export function useDocumentForm<V extends object, R>(opts: DocumentFormOptions<V, R>) {
  const [values, setValues] = useState<V>(opts.initial);
  const [errors, setErrors] = useState<FieldErrors<V>>({});
  const [formError, setFormError] = useState<string | null>(null);
  // Ключ живёт, пока значения те же: повтор после обрыва сети сервер узнает,
  // правка поля даёт новую попытку с новым ключом.
  const [key, setKey] = useState(() => crypto.randomUUID());
  const save = useMutation({ mutationFn: (v: V) => opts.submit(v, key) });

  const set = <K extends keyof V>(k: K, v: V[K]) => {
    setValues((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
    setKey(crypto.randomUUID());
  };

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    setFormError(null);
    const local = opts.validate?.(values) ?? {};
    if (Object.values(local).some(Boolean)) {
      setErrors(local);
      return;
    }
    save.mutate(values, {
      onSuccess: (r) => {
        setKey(crypto.randomUUID());
        opts.onSuccess(r);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.problem?.code) {
          const args = (err.problem.args ?? {}) as Record<string, unknown>;
          const field = opts.fieldOf ? opts.fieldOf(err.problem.code, args) : (args.field as (keyof V & string) | undefined);
          const text = errorNotice(err).title;
          if (field && field in values) {
            setErrors((prev) => ({ ...prev, [field]: text }));
            return;
          }
        }
        setFormError(errorNotice(err).title);
      },
    });
  };

  return { values, set, errors, formError, pending: save.isPending, handleSubmit };
}

/** Текст правила по коду для проверки в браузере тем же каталогом, что у сервера. */
export const ruleText = (code: string, args?: Record<string, string | number>): string => serverErrorText(code, args) ?? code;

export interface FormPageProps {
  title: string;
  /** Куда ведут «назад» и «Скасувати»: обычно список документов. */
  back: NonNullable<LinkProps["to"]>;
  submitLabel: string;
  form: { formError: string | null; pending: boolean; handleSubmit: (e: SubmitEvent) => void };
  children: ReactNode;
  /** Под формой: связанные действия со своими запросами (версии изделия у спецификации). */
  after?: ReactNode;
}

/** Страница формы: шапка с «назад», ошибка формы, поля, «Зберегти» и «Скасувати». */
export function FormPage({ title, back, submitLabel, form, children, after }: FormPageProps) {
  return (
    <div className="page stock-narrow doc-form">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to={back} aria-label={t("docs.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{title}</h1>
      </div>
      {form.formError ? <Notice tone="danger" title={form.formError} className="stock-notice" /> : null}
      <Plate cut="lg">
        <form className="stock-form" onSubmit={form.handleSubmit} noValidate>
          {children}
          <div className="doc-form-actions">
            <Button type="submit" variant="primary" disabled={form.pending}>
              {submitLabel}
            </Button>
            <Button asChild variant="ghost">
              <Link to={back}>{t("docs.cancel")}</Link>
            </Button>
          </div>
        </form>
      </Plate>
      {after}
    </div>
  );
}
